// Cloudinary Configuration and Service
import { CLOUDINARY_CONFIG, validateCloudinaryConfig } from '../config/cloudinary.config';

interface CloudinaryConfig {
    cloudName: string;
    uploadPreset: string;
    apiKey?: string;
    apiSecret?: string;
}

// Use configuration from config file
const cloudinaryConfig: CloudinaryConfig = {
    cloudName: CLOUDINARY_CONFIG.CLOUD_NAME,
    uploadPreset: CLOUDINARY_CONFIG.UPLOAD_PRESET,
    apiKey: CLOUDINARY_CONFIG.API_KEY,
    apiSecret: CLOUDINARY_CONFIG.API_SECRET,
};

// Cloudinary upload response interface
interface CloudinaryUploadResponse {
    public_id: string;
    secure_url: string;
    width: number;
    height: number;
    format: string;
    resource_type: string;
    created_at: string;
    bytes: number;
    etag: string;
    version: number;
    signature: string;
}

// Cloudinary service class
class CloudinaryService {
    private config: CloudinaryConfig;

    constructor(config: CloudinaryConfig) {
        this.config = config;

        // Validate configuration on initialization
        if (!validateCloudinaryConfig()) {
            console.warn('⚠️  Cloudinary configuration incomplete. Some features may not work properly.');
        }
    }

    /**
     * Upload image to Cloudinary
     * @param file - File to upload
     * @param folder - Optional folder to organize uploads
     * @returns Promise with upload response
     */
    async uploadImage(file: File, folder?: string): Promise<CloudinaryUploadResponse> {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_preset', this.config.uploadPreset);

            if (folder) {
                formData.append('folder', folder);
            }

            const response = await fetch(
                `https://api.cloudinary.com/v1_1/${this.config.cloudName}/image/upload`,
                {
                    method: 'POST',
                    body: formData,
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`Upload failed: ${errorData.error?.message || response.statusText}`);
            }

            const result: CloudinaryUploadResponse = await response.json();
            return result;
        } catch (error) {
            console.error('Cloudinary upload error:', error);
            throw new Error(`Failed to upload image: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Upload multiple images
     * @param files - Array of files to upload
     * @param folder - Optional folder to organize uploads
     * @returns Promise with array of upload responses
     */
    async uploadMultipleImages(files: File[], folder?: string): Promise<CloudinaryUploadResponse[]> {
        try {
            const uploadPromises = files.map(file => this.uploadImage(file, folder));
            const results = await Promise.all(uploadPromises);
            return results;
        } catch (error) {
            console.error('Multiple upload error:', error);
            throw new Error(`Failed to upload images: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Delete image from Cloudinary
     * @param publicId - Public ID of the image to delete
     * @returns Promise with deletion result
     */
    async deleteImage(publicId: string): Promise<{ result: string }> {
        try {
            if (!this.config.apiKey || !this.config.apiSecret) {
                throw new Error('API key and secret required for deletion');
            }

            // Generate signature for deletion
            const timestamp = Math.round(new Date().getTime() / 1000);
            const signature = this.generateSignature(publicId, timestamp);

            const response = await fetch(
                `https://api.cloudinary.com/v1_1/${this.config.cloudName}/image/destroy`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        public_id: publicId,
                        signature: signature,
                        api_key: this.config.apiKey,
                        timestamp: timestamp,
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`Deletion failed: ${errorData.error?.message || response.statusText}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Cloudinary deletion error:', error);
            throw new Error(`Failed to delete image: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Generate transformation URL for image
     * @param publicId - Public ID of the image
     * @param transformations - Cloudinary transformation parameters
     * @returns Transformed image URL
     */
    getTransformedUrl(publicId: string, transformations: Record<string, any> = {}): string {
        const baseUrl = `https://res.cloudinary.com/${this.config.cloudName}/image/upload`;
        const transformString = Object.entries(transformations)
            .map(([key, value]) => `${key}_${value}`)
            .join(',');

        return transformString
            ? `${baseUrl}/${transformString}/${publicId}`
            : `${baseUrl}/${publicId}`;
    }

    /**
     * Generate signature for authenticated requests
     * @param publicId - Public ID
     * @param timestamp - Timestamp
     * @returns Signature string
     */
    private generateSignature(publicId: string, timestamp: number): string {
        if (!this.config.apiSecret) {
            throw new Error('API secret required for signature generation');
        }

        // Simple signature generation (you might want to use a proper crypto library)
        const stringToSign = `public_id=${publicId}&timestamp=${timestamp}${this.config.apiSecret}`;
        // Note: In a real implementation, you should use proper HMAC-SHA1
        return btoa(stringToSign);
    }

    /**
     * Get configuration
     */
    getConfig(): CloudinaryConfig {
        return { ...this.config };
    }

    /**
     * Update configuration
     */
    updateConfig(newConfig: Partial<CloudinaryConfig>): void {
        this.config = { ...this.config, ...newConfig };
    }
}

// Create and export service instance
export const cloudinaryService = new CloudinaryService(cloudinaryConfig);

// Export types and class for advanced usage
export type { CloudinaryConfig, CloudinaryUploadResponse };
export { CloudinaryService };

// Helper functions for common use cases
export const uploadProfileImage = async (file: File, userId: string) => {
    return cloudinaryService.uploadImage(file, `${CLOUDINARY_CONFIG.FOLDERS.PROFILE_IMAGES}/${userId}`);
};

export const uploadStudentImage = async (file: File, studentId: string) => {
    return cloudinaryService.uploadImage(file, `${CLOUDINARY_CONFIG.FOLDERS.STUDENTS}/${studentId}`);
};

export const uploadStaffImage = async (file: File, staffId: string) => {
    return cloudinaryService.uploadImage(file, `${CLOUDINARY_CONFIG.FOLDERS.STAFF}/${staffId}`);
};

export const uploadTeacherImage = async (file: File, teacherId: string) => {
    return cloudinaryService.uploadImage(file, `${CLOUDINARY_CONFIG.FOLDERS.TEACHERS}/${teacherId}`);
};

// Utility function to get optimized image URL
export const getOptimizedImageUrl = (publicId: string, width?: number, height?: number) => {
    const transformations: Record<string, any> = {};

    if (width) transformations.w = width;
    if (height) transformations.h = height;

    // Add quality optimization
    transformations.q = 'auto';
    transformations.f = 'auto';

    return cloudinaryService.getTransformedUrl(publicId, transformations);
};

// Utility function to get thumbnail URL
export const getThumbnailUrl = (publicId: string, size: number = 150) => {
    return cloudinaryService.getTransformedUrl(publicId, {
        w: size,
        h: size,
        c: 'fill',
        q: 'auto',
        f: 'auto'
    });
};

// Utility function to get profile thumbnail using config
export const getProfileThumbnail = (publicId: string) => {
    return cloudinaryService.getTransformedUrl(publicId, CLOUDINARY_CONFIG.TRANSFORMATIONS.PROFILE_THUMBNAIL);
};

// Utility function to get profile large image using config
export const getProfileLarge = (publicId: string) => {
    return cloudinaryService.getTransformedUrl(publicId, CLOUDINARY_CONFIG.TRANSFORMATIONS.PROFILE_LARGE);
};

export const deleteStudentImage = async (publicId: string) => {
    return cloudinaryService.deleteImage(publicId);
};

export const deleteStaffImage = async (publicId: string) => {
    return cloudinaryService.deleteImage(publicId);
};

export const deleteTeacherImage = async (publicId: string) => {
    return cloudinaryService.deleteImage(publicId);
};
